import { _ftCamField } from '../../shared/campaign-field';
import { ftClockCalendarFromArr, ftClockColsFromArr, ftCampaignTimeAddSeconds, type CampaignClockCols } from '../../shared/campaign-clock';
import { readItemField } from '../compile/item-field-reader';
import { pictureListOf } from '../../shared/array-types';
import { createVitalityReader } from '../compile/vitality-reader';
import type { JanusCtx, Row } from '../ability/context';
import { appendSpawnStatus, type SpawnReaders, type SpawnRowPlan } from '../spawn/row-plan';
import { getStatusEffectDurationTimes, type StatusEnds } from '../ability/statuses/get-status-effect-duration-times';
import { readLayerListEntryUuid } from '../compile/inventory-raw-readers';
import { baseSweetSpotWidth, randomSweetSpot, resolveDiceCount } from '../minigames/lockpick';

const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;

export interface InventoryBirthOptions {
    spoilage?: { int: number; type: string };
    inventorySize?: string;
    weightOverride?: number;
    position?: { x: number; y: number; z: number };
}

export const SPOILING_STATUS_ID = 'F7A1C3B2-5D64-4E18-9A70-2C8B41D5E093';

/** Add and authoring births share the same newly rolled lock puzzle. */
export function inventoryBirthLockpick(definition: Row, value: JanusCtx['read']['value'], die: (sides: number) => number): Row {
    if (readItemField(definition, 'item_type') !== 'lock'
        && readLayerListEntryUuid(value(definition, 'roll_type')) !== 'CC2CEB6B-CEFB-481F-911A-A47A427D5EB2') return {};
    const [start, end] = randomSweetSpot(baseSweetSpotWidth(resolveDiceCount(value(definition, 'lockpick_difficulty'), value(definition, 'dice'))), () => (die(1000000) - 1) / 1000000);
    return { lockpick_degree_start: start, lockpick_degree_end: end };
}

/** A source lifetime replaces the entire item pair, including for packed/prefab descendants. */
export function inventoryBirthSpoilage(definition: Row, value: JanusCtx['read']['value'], options: InventoryBirthOptions = {}): InventoryBirthOptions['spoilage'] | null {
    const amount = Math.trunc(Number(options.spoilage ? options.spoilage.int : value(definition, 'spoilage_int')));
    const type = String(options.spoilage ? options.spoilage.type : value(definition, 'spoilage_type') ?? '').trim().toLowerCase();
    return Number.isSafeInteger(amount) && amount > 0 && ['seconds', 'rounds', 'minutes', 'hours', 'days', 'weeks', 'months', 'years'].includes(type)
        ? { int: amount, type } : null;
}

/** Finite births require a usable clock; never save an all-zero, accidentally permanent deadline. */
export function inventoryBirthDeadline(definition: Row, holder: Row, campaign: Row | null,
    value: JanusCtx['read']['value'], options: InventoryBirthOptions = {}, currentClock?: () => CampaignClockCols | null): StatusEnds | null {
    const spoilage = inventoryBirthSpoilage(definition, value, options);
    if (!spoilage) return null;
    const duration = inventoryDurationReaders(campaign, value, currentClock);
    if (!duration.campaignClock()) throw new Error('inventory-birth:campaign-clock-required');
    return getStatusEffectDurationTimes({ read: duration },
        { item_layer_types: { duration_int: spoilage.int, duration_type: spoilage.type } }, holder);
}

export function appendInventoryBirthSpoilage(plan: SpawnRowPlan, read: Pick<SpawnReaders, 'uuid' | 'clock'>, target: string, ends: StatusEnds): void {
    appendSpawnStatus(plan, read, target, target, SPOILING_STATUS_ID, '', { ...ends, connection: read.uuid() });
}

/** The page clock/calendar readers also accept wrapped campaign light, sun and cycle fields. */
export function inventoryDurationReaders(campaign: Row | null, value: JanusCtx['read']['value'], currentClock?: () => CampaignClockCols | null):
    Pick<JanusCtx['read'], 'has' | 'value' | 'campaignClock' | 'addCampaignSeconds'> {
    const calendar = ftClockCalendarFromArr(campaign);
    return { value, has: (row, key) => value(row, key) != null,
        campaignClock: () => _ftCamField(campaign, 'campaign_id') && calendar.months.length
            ? currentClock?.() ?? ftClockColsFromArr(campaign) : null,
        addCampaignSeconds: (clock, seconds) => ftCampaignTimeAddSeconds(clock, calendar, seconds) };
}

/** Shared birth defaults read hydrated vitality; lean base_v_* fields must not become the fallback five. */
export function inventoryBirthVitality(definition: Row): Row {
    const health = record(createVitalityReader({ resolveInventoryInstance: () => null })(definition));
    const maximum = readItemField(health, 'v_max') ?? 5;
    return { v_max: maximum, v_current: readItemField(health, 'v_current') ?? maximum,
        v_temp: readItemField(health, 'v_temp') ?? 0, v_max_temp: readItemField(health, 'v_max_temp') ?? 0 };
}

export function inventoryBirthPicture(definition: Row, choose?: (count: number) => number): string | null {
    const pictures = (pictureListOf(definition, ['item']) as Row[]).map(row => readItemField(row, 'picture_id'))
        .filter((id): id is string => typeof id === 'string' && id !== '');
    if (!pictures.length) return null;
    return choose ? pictures[choose(pictures.length) - 1] ?? null : pictures.sort()[0] ?? null;
}
